import { createFileRoute, SearchSchemaInput, useNavigate } from '@tanstack/react-router'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { useEffect, useState } from 'react'
import { DatePicker } from "@/components/ui/datepicker"
import { useQuery } from '@tanstack/react-query'
import { Api, searchSubStr } from '@/lib/utils'
import { Issue } from '@/lib/interfaces'
import dayjs from 'dayjs'
import { HelpCircle } from 'lucide-react'
import searchIcon from "@/assets/paytm/search.png"
import dismissIcon from "@/assets/paytm/dismiss.png"
import IssueDetails from '@/components/features/issues/issue-details'

export const Route = createFileRoute('/shikhar/help')({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { date: string } => {
        return {
            date: search.date as string || dayjs().format('YYYY-MM-DD')
        }
    }
})

function RouteComponent() {
    const navigate = useNavigate({ from: Route.fullPath })
    const { setPaytmTab, accountId, token } = useStore(useShallow(state => ({
        setPaytmTab: state.setPaytmTab,
        accountId: state.selectedAccount.id,
        token: state.token
    })))
    const { date } = Route.useSearch()
    const [searchFilter, setSearchFilter] = useState('')

    useEffect(() => {
        setPaytmTab('Help')
    }, [])

    const getIssues = useQuery({
        queryKey: ['issues', date, token],
        queryFn: () => Api<Issue[]>('/webui/issues_detailed', "POST", { date }, { token }),
        staleTime: 5 * 60 * 1000,
        refetchInterval: 5 * 60 * 1000,
        refetchOnWindowFocus: false
    })

    const [showSupportNote, setShowSupportNote] = useState(false)

    return <div className='absolute left-0 right-0 top-0 bottom-0'>
        <div className="h-14 flex justify-between px-4 items-center bg-light-blue">
            <div className="flex items-center">
                <p className="font-semibold text-lg">Support</p>
            </div>
        </div>
        <div className='flex items-center justify-between m-4'>
            <DatePicker date={dayjs(date).toDate()} onChange={dateValue => {
                navigate({
                    search: () => ({ date: dayjs(dateValue).format('YYYY-MM-DD') }),
                })
            }} />
        </div>
        <div className="flex items-center p-2 border border-black rounded-lg m-4 justify-between">
            <div className="flex items-center w-full">
                <img src={searchIcon} className="w-6 mr-2" />
                <input
                    className="border-none outline-none text-sm font-medium w-full"
                    placeholder="Search by order number or issue id"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                />
            </div>
            <img
                src={dismissIcon}
                className={`w-6 ${!searchFilter ? 'opacity-20' : ''}`}
                onClick={() => setSearchFilter('')}
            />
        </div>
        <div className="absolute top-[170px] bottom-20 overflow-auto w-full p-4 *:mb-4">
            {[...(getIssues.data || [])]
                .filter(issue => !searchFilter || searchSubStr(searchFilter, [issue.issueId, issue.orderId, issue.clientOrderId]).length > 0)
                .map(issue => (
                    <IssueDetails
                        key={issue.issueId}
                        billNumber={issue.clientOrderId || issue.orderId || issue.issueId}
                        refundAmount={(issue.refundAmountGivenToClient || issue.refundAmount || 0).toString()}
                        createdAt={dayjs(issue.createdat).format('D MMM, hh:mm A')}
                        status={issue.resolutionStatus || issue.issueStatus}
                        issueId={issue.issueId}
                        description={issue.shortDescription || issue.longDescription || '--'}
                        onClick={() => navigate({
                            to: '/shikhar/issues/$issueId',
                            params: { issueId: issue.issueId },
                            search: { date }
                        })}
                    />
                ))}
        </div>
        <div className="fixed bottom-20 right-4 z-40">
            {showSupportNote ? (
                <div className="w-80 rounded-2xl border bg-white p-4 shadow-3xl text-sm text-gray-700">
                    <div className="flex items-start justify-between gap-3">
                        <p className="font-semibold text-gray-900">Support</p>
                        <button
                            type="button"
                            className="text-gray-500"
                            onClick={() => setShowSupportNote(false)}
                            aria-label="Close support note"
                        >
                            ✕
                        </button>
                    </div>
                    <p className="mt-2">
                        For all order related issues, please raise the issue so that the Logistics Service Providers can respond.
                    </p>
                    <p className="mt-2">
                        For any other issues, please write to us at{' '}
                        <span className="font-semibold">merchant-support@prorouting.in</span> and quote your account Id{' '}
                        <span className="font-semibold">{accountId}</span>.
                    </p>
                </div>
            ) : null}
            <button
                type="button"
                className="ml-auto mt-3 flex h-12 w-12 items-center justify-center rounded-full bg-black text-white shadow-3xl cursor-pointer"
                onClick={() => setShowSupportNote(value => !value)}
                aria-label="Support info"
            >
                <HelpCircle className="h-6 w-6" />
            </button>
        </div>
    </div>
}
